import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { beatBridge } from '../state/beatBridge';
import { withRepeat } from '../utils/facadeTexture';
import { useOptionalTexture } from '../utils/useOptionalTexture';

// The park: kids playing + chess tables by the basketball court.
// Crowd figures move to tempo (v0.2 §5 — "crowd figures move to tempo").
function KidFigure({ position, beatOffset = 0, color = '#3f7d53' }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current) {
      const hop = Math.abs(Math.sin(((beatBridge.phase + beatOffset) % 1) * Math.PI * 2));
      ref.current.position.y = position[1] + hop * 0.25;
    }
  });
  return (
    <group position={position}>
      <mesh ref={ref} castShadow>
        <capsuleGeometry args={[0.28, 0.7, 4, 12]} />
        <meshStandardMaterial color={color} roughness={0.8} />
      </mesh>
    </group>
  );
}

export default function Park(props) {
  // Gemini texture pack (procedural/plain color until generated). Memoized —
  // Park re-renders every frame on the beat clock.
  const grassBase = useOptionalTexture('/textures/grass.jpg', null);
  const grassMap = useMemo(() => (grassBase ? withRepeat(grassBase, 8, 5.2) : null), [grassBase]);
  const courtMap = useOptionalTexture('/textures/court.jpg', null); // one-shot decal, no repeat
  return (
    <group {...props}>
      {/* Grass — polygonOffset keeps the ground decal stack (road → grass →
          court) from z-fighting at district distance, where centimeter y-gaps
          fall below depth-buffer precision and flicker. */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]}>
        <planeGeometry args={[40, 26]} />
        <meshStandardMaterial color={grassMap ? '#b9c4ae' : '#2f5d33'} roughness={1} map={grassMap} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
      </mesh>

      {/* Basketball court */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[8, 0.1, 2]}>
        <planeGeometry args={[15, 11]} />
        <meshStandardMaterial color={courtMap ? '#d8d8d8' : '#b8552f'} roughness={0.95} map={courtMap} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      {/* Hoop */}
      <group position={[14.5, 0, 2]}>
        <mesh castShadow position={[0, 1.9, 0]}>
          <cylinderGeometry args={[0.08, 0.08, 3.8, 8]} />
          <meshStandardMaterial color="#444" metalness={0.7} roughness={0.4} />
        </mesh>
        <mesh castShadow position={[-0.4, 3.6, 0]}>
          <boxGeometry args={[0.05, 1.1, 1.8]} />
          <meshStandardMaterial color="#eee" roughness={0.6} />
        </mesh>
        <mesh position={[-0.75, 3.05, 0]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.45, 0.03, 8, 24]} />
          <meshStandardMaterial color="#d43d2a" metalness={0.6} roughness={0.4} />
        </mesh>
      </group>

      {/* Kids playing (beat-hop figures) */}
      <KidFigure position={[6, 0.7, 1]} color="#4f9d69" />
      <KidFigure position={[10, 0.7, 3]} beatOffset={0.25} color="#3f7d9d" />
      <KidFigure position={[9, 0.7, -1]} beatOffset={0.5} color="#9d3f6b" />

      {/* Chess tables by the court */}
      {[[-6, -2], [-3, -2]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh castShadow position={[0, 0.75, 0]}>
            <boxGeometry args={[1.4, 0.08, 1.4]} />
            <meshStandardMaterial color="#6b4a2f" roughness={0.9} />
          </mesh>
          <mesh castShadow position={[0, 0.37, 0]}>
            <cylinderGeometry args={[0.09, 0.13, 0.75, 8]} />
            <meshStandardMaterial color="#3a3a3a" roughness={0.9} />
          </mesh>
          {/* Two seated figures */}
          <mesh castShadow position={[0.9, 0.5, 0]}>
            <capsuleGeometry args={[0.3, 0.6, 4, 12]} />
            <meshStandardMaterial color="#5a4632" roughness={0.9} />
          </mesh>
          <mesh castShadow position={[-0.9, 0.5, 0]}>
            <capsuleGeometry args={[0.3, 0.6, 4, 12]} />
            <meshStandardMaterial color="#324a5a" roughness={0.9} />
          </mesh>
        </group>
      ))}
    </group>
  );
}